import { Box, Card, CardContent, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { commissionService, commissionAdjustmentService, commissionCheckpointService, commissionGoalProgressService, employeeWalletService, ICommissionCheckpointView } from '../../../shared/services';
import { IPropsEmployeePage } from '../Employee';
import { dateFormat } from '../../../shared/helpers/dateFormat';
import { formatCurrency } from '../../../shared/helpers/formatCurrency';
import { DatePreset, DatePresetSelector, getPresetRange, SummaryCard, CenteredLoading, CommissionSummaryRow, CommissionEmployeeExpandable } from '../../../shared/components';

const AdjustmentTypeNames: Record<number, string> = {
  0: 'Bonificação',
  1: 'Desconto',
  2: 'Vale / Adiantamento',
};

export const EmployeeCommissions: React.FC<IPropsEmployeePage> = ({ employee }) => {
  const [preset, setPreset] = useState<DatePreset>('week');
  const presetsFilter: DatePreset[] = ['week', 'month', 'all'];
  const [expanded, setExpanded] = useState((employee.businessEmployees?.length ?? 0) === 1);
  const [checkpoints, setCheckpoints] = useState<ICommissionCheckpointView[]>([]);
  const [loadingCheckpoints, setLoadingCheckpoints] = useState(false);
  const checkpointsLoadedRef = useRef(false);

  const businessEmployee = employee.businessEmployees?.[0];
  const businessId = businessEmployee?.businessId;

  const { start, end } = useMemo(() => getPresetRange(preset), [preset]);

  const loadCheckpoints = useCallback(async (employeeBizId: number) => {
    setLoadingCheckpoints(true);
    try {
      const data = await commissionCheckpointService.getCheckpoints(employeeBizId, 5);
      setCheckpoints(data);
    } catch {
      // silently fail
    } finally {
      setLoadingCheckpoints(false);
    }
  }, []);

  // Carrega checkpoints na montagem (expandido por padrão)
  useEffect(() => {
    if (businessEmployee?.id && !checkpointsLoadedRef.current) {
      checkpointsLoadedRef.current = true;
      loadCheckpoints(businessEmployee.id);
    }
  }, [businessEmployee?.id, loadCheckpoints]);

  // ── Endpoint unificado ──
  const { data: commissions, isFetching } = useQuery({
    queryKey: ['employeeCommissions', employee.id, start, end],
    queryFn: () => commissionService.getCommissions(businessId!, businessEmployee?.id, start, end),
    enabled: !!businessId && !!businessEmployee?.id,
  });

  // Deriva do resultado unificado (primeiro elemento, já que filtramos por employee)
  const employeeData = useMemo(() => commissions?.[0] ?? null, [commissions]);

  const employeeSummary = useMemo(() => {
    if (!employeeData) return [];
    return [{
      businessEmployeeId: employeeData.businessEmployeeId,
      employeeName: employeeData.employeeName,
      totalAppointments: employeeData.totalAppointments,
      grossRevenue: employeeData.totalGrossRevenue,
      cardFees: employeeData.totalCardFees,
      netRevenue: employeeData.totalNetRevenue,
      servicesCommissionRate: employeeData.servicesCommissionRate,
      commissionAmount: employeeData.totalCommission,
    }];
  }, [employeeData]);

  const { data: adjustments, isFetching: isFetchingAdjustments } = useQuery({
    queryKey: ['employeeCommissionAdjustments', employee.id],
    queryFn: () => commissionAdjustmentService.getByBusiness(businessId!, businessEmployee?.id),
    enabled: !!businessId && !!businessEmployee?.id,
  });

  const { data: goalProgress } = useQuery({
    queryKey: ['employeeGoalProgress', businessEmployee?.id],
    queryFn: () => commissionGoalProgressService.getGoalProgress(businessEmployee!.id),
    enabled: !!businessEmployee?.id,
  });

  // ── Wallet: saldo pendente e já pago ──
  const { data: wallet } = useQuery({
    queryKey: ['employeeWallet', employee.id],
    queryFn: () => employeeWalletService.getByEmployeeId(employee.id),
    enabled: !!employee.id,
  });

  const totalPago = useMemo(() =>
    (wallet?.payoutRequests
      ?.filter(pr => pr.status === 1)
      ?.reduce((s, pr) => s + pr.amount, 0) ?? 0),
  [wallet]);

  const pendente = wallet?.pendingCommissions ?? 0;

  const payoutHistory = useMemo(() =>
    (wallet?.payoutRequests
      ?.filter(pr => pr.status === 1)
      ?.map(pr => ({
        id: pr.id,
        amount: pr.amount,
        status: pr.status,
        statusName: pr.statusName,
        requestedAt: pr.requestedAt,
        paidAt: pr.paidAt,
        notes: pr.notes,
      })) ?? []), [wallet]);

  const totalCommission = useMemo(() => employeeSummary.reduce((t, e) => t + e.commissionAmount, 0), [employeeSummary]);
  const totalGrossRevenue = useMemo(() => employeeSummary.reduce((t, e) => t + e.grossRevenue, 0), [employeeSummary]);

  const isEmpty = !isFetching && employeeSummary.length === 0;

  if (!businessId) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <Typography color='text.secondary'>Nenhum estabelecimento vinculado.</Typography>
      </Box>
    );
  }

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {/* Period selector */}
      <DatePresetSelector value={preset} onChange={setPreset} presets={presetsFilter} />

      {/* Summary Cards */}
      <SummaryCard
        cards={[
          { title: 'Minha Comissão', value: formatCurrency(totalCommission), color: 'primary.main' },
          { title: 'Receita Bruta', value: formatCurrency(totalGrossRevenue), color: 'success.main' },
          { title: 'Já Recebido', value: formatCurrency(totalPago), color: 'info.main' },
          { title: 'A Receber', value: formatCurrency(pendente), color: 'warning.main' },
        ]}
      />

      {/* Loading state */}
      {isFetching ? (
        <CenteredLoading />
      ) : isEmpty ? (
        <Typography variant='body2' color='text.secondary' textAlign='center' py={4}>
          Nenhum dado de comissão encontrado para este período.
        </Typography>
      ) : (
        <>
          {/* Synthetic table with expandable detail */}
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox' />
                  <TableCell>Período</TableCell>
                  <TableCell align='right'>Atend.</TableCell>
                  <TableCell align='right'>Receita Bruta</TableCell>
                  <TableCell align='right'>Taxa Cartão</TableCell>
                  <TableCell align='right'>Líquido</TableCell>
                  <TableCell align='right'>%</TableCell>
                  <TableCell align='right'>Comissão</TableCell>
                  <TableCell align='right'>Já Recebido</TableCell>
                  <TableCell align='right'>A Receber</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employeeSummary.map((item) => (
                  <Fragment key={item.businessEmployeeId}>
                    <CommissionSummaryRow
                      employeeName={item.employeeName}
                      totalAppointments={item.totalAppointments}
                      grossRevenue={item.grossRevenue}
                      cardFees={item.cardFees}
                      netRevenue={item.netRevenue}
                      servicesCommissionRate={item.servicesCommissionRate}
                      commissionAmount={item.commissionAmount}
                      totalPago={totalPago}
                      pendente={pendente}
                      isExpanded={expanded}
                      onToggle={() => {
                        const next = !expanded;
                        setExpanded(next);
                        if (next && businessEmployee) {
                          loadCheckpoints(businessEmployee.id);
                        }
                      }}
                    />
                    <TableRow key={`${item.businessEmployeeId}-expand`}>
                      <TableCell sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }} colSpan={10}>
                        <CommissionEmployeeExpandable
                          expanded={expanded}
                          payoutHistory={payoutHistory}
                          items={employeeData?.items ?? []}
                          totalGrossRevenue={employeeData?.totalGrossRevenue ?? 0}
                          totalCommission={employeeData?.totalCommission ?? 0}
                          checkpoints={checkpoints}
                          loadingCheckpoints={loadingCheckpoints}
                          goalProgress={goalProgress}
                        />
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Adjustments */}
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>Meus Ajustes</Typography>

              {isFetchingAdjustments ? (
                <Box display='flex' justifyContent='center' py={2}><CircularProgress size={24} /></Box>
              ) : adjustments && adjustments.length > 0 ? (
                <Box display='flex' flexDirection='column' gap={1}>
                  {adjustments.map((adj) => (
                    <Box
                      key={adj.id}
                      display='flex'
                      justifyContent='space-between'
                      alignItems='center'
                      p={1.5}
                      sx={{ borderRadius: '8px', backgroundColor: 'action.hover' }}
                    >
                      <Box>
                        <Typography variant='body2' fontWeight={600}>
                          {AdjustmentTypeNames[adj.type] || 'Ajuste'}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {adj.description} — {dateFormat(adj.createdAt, 'DD/MM')}
                        </Typography>
                      </Box>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Typography
                          variant='body2'
                          fontWeight={700}
                          sx={{ color: adj.type === 1 ? 'error.main' : 'success.main' }}
                        >
                          {adj.type === 1 ? '-' : '+'}{formatCurrency(adj.amount)}
                        </Typography>
                        {adj.type === 2 && (
                          <Chip
                            label={adj.deducted ? 'Deduzido' : 'Pendente'}
                            size='small'
                            color={adj.deducted ? 'success' : 'warning'}
                            variant='outlined'
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Nenhum ajuste registrado.
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};
